import type { ReactNode } from 'react'
import { useFieldContext } from '@/lib/form-contexts'
import { Checkbox, type CheckboxProps } from '../ui/checkbox'
import { Label } from '../ui/label'
import FieldInfo from './FieldInfo'
import { useIsReadOnly } from '@/lib/casl'

export type CheckboxGroupItem = {
  id: string
  name: ReactNode
}

export type CheckboxGroupInputProps = CheckboxProps & {
  label?: ReactNode
  items: CheckboxGroupItem[]
}

export default function CheckboxGroupInput({
  label,
  items,
  disabled = false,
  ...props
}: CheckboxGroupInputProps) {
  const field = useFieldContext<string[]>()
  const { isReadOnly } = useIsReadOnly()

  return (
    <>
      <div className="grid grid-cols-1 gap-y-6">
        <Label>{label}</Label>
        {items.map((item) => (
          <div key={item.id} className="flex flex-col gap-y-2 w-full">
            <div className="inline-flex items-center gap-2">
              <Label htmlFor={item.id}>
                <Checkbox
                  id={item.id}
                  name={field.name}
                  // The Checkbox component is a button under the hood
                  // It needs type="buttom" to prevent accidental form submissions
                  type="button"
                  checked={field.state.value?.includes(item.id)}
                  onCheckedChange={(isChecked) => {
                    field.handleChange((prevValue) =>
                      isChecked ? [...prevValue, item.id] : prevValue.filter((v) => v !== item.id)
                    )
                  }}
                  disabled={isReadOnly || disabled}
                  {...props}
                />
                {item.name}
              </Label>
            </div>
          </div>
        ))}
      </div>
      {<FieldInfo />}
    </>
  )
}
